// src/pages/BottlingPlanDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Import reusable components
import BottlingPlanHeader from '../components/bottling/BottlingPlanHeader';
import PlanDetailsForm from '../components/bottling/PlanDetailsForm';
import BottlingItemsTable from '../components/bottling/BottlingItemsTable';
import BottlingItemModal from '../components/bottling/BottlingItemModal';
import DeleteConfirmationModal from '../components/bottling/DeleteConfirmationModal';
import MaterialsSummary from '../components/MaterialsSummary';
import MaterialOrderModal from '../components/Modals/MaterialOrderModal';

// Import services and hooks
import { bottlingPlanService } from '../services/bottlingPlanService';
import { useBottlingCalculations } from '../hooks/useBottlingCalculations';

const BottlingPlanDetail = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  
  // Plan and related data state
  const [plan, setPlan] = useState({
    planDate: '',
    status: 'Draft',
    bottlingDate: '',
    performedBy: '',
    notes: '',
    items: []
  });
  
  // Reference data state
  const [wines, setWines] = useState([]);
  const [brands, setBrands] = useState([]);
  const [fancifulNames, setFancifulNames] = useState([]);
  const [materialsByType, setMaterialsByType] = useState({
    bottles: [],
    corks: [],
    capsules: [],
    labels: []
  });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Modal state
  const { calculateEstimatedCases, calculateMaterialsNeeded } = useBottlingCalculations(materialsByType);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    wineID: '',
    gallonsPlanned: '',
    bottleID: '',
    corkID: '',
    capsuleID: '',
    labelID: '',
    notes: '',
    finalAlc: '',
    brandID: '',
    fancifulNameID: '',
    version: ''
  });
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const fetchWineDetails = async (wineId) => {
    try {
      const response = await axios.get(`/api/wines/${wineId}`);
      return response.data;
    } catch (err) {
      console.error(`Error fetching wine details for ID ${wineId}:`, err);
      return null;
    }
  };
  
  // Utility functions for date validation
  const areDatesSet = () => {
    if (!plan.planDate || !plan.bottlingDate) return false;

    const planDate = new Date(plan.planDate);
    const bottlingDate = new Date(plan.bottlingDate);
    const currentDate = new Date();
    
    // Remove time part for accurate date comparison
    currentDate.setHours(0, 0, 0, 0);
    
    // Check if bottling date is in the past
    const isBottlingDateFuture = bottlingDate >= currentDate;
    
    // Return true only if bottling date is after plan date AND not in the past
    return bottlingDate >= planDate && isBottlingDateFuture;
  };

  const getDateWarningMessage = () => {
    if (!plan.planDate || !plan.bottlingDate) {
      return "Plan Date and Bottling Date must be set before adding items";
    }
    
    const bottlingDate = new Date(plan.bottlingDate);
    const currentDate = new Date();
    
    // Remove time part for accurate date comparison
    currentDate.setHours(0, 0, 0, 0);
    
    if (bottlingDate < currentDate) {
      return "Bottling Date must be a future date to add items";
    }
    
    const planDate = new Date(plan.planDate);
    if (bottlingDate < planDate) {
      return "Bottling Date must be after Plan Date";
    }
    
    return "";
  };
  
  // Fetch all necessary data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch reference data in parallel
        const [
          winesRes,
          materialsRes,
          brandsRes,
          fancifulRes
        ] = await Promise.all([
          axios.get('/api/wines', { params: { active: 1, limit: 100 } }),
          bottlingPlanService.getMaterialsStock(),
          axios.get('/api/brands'),
          axios.get('/api/fanciful-names')
        ]);
        
        setMaterialsByType(materialsRes);
        setWines(winesRes.data.wines);
        setBrands(brandsRes.data);
        setFancifulNames(fancifulRes.data);

        // If editing an existing plan, fetch its details
        if (planId && planId !== 'new') {
          const planData = await bottlingPlanService.getPlan(planId);
          
          // Format dates for input fields
          const formattedPlan = {
            ...planData,
            planDate: formatDateForInput(planData.planDate),
            bottlingDate: formatDateForInput(planData.bottlingDate)
          };
          
          setPlan(formattedPlan);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch required data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [planId]);

  useEffect(() => {
    if (materialsByType?.bottles?.length > 0) {
      // Test calculation with known values
      const testGallons = 232;
      const kegId = materialsByType.bottles.find(b => b.name.includes('Keg'))?.materialID;
      if (kegId) {
        const result = calculateEstimatedCases(testGallons, kegId);
        console.log(`TEST CALCULATION: ${testGallons} gallons in keg (ID: ${kegId}) = ${result} units`);
      }
    }
  }, [materialsByType, calculateEstimatedCases]);

  // Helper Functions
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Form Event Handlers
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (planId && planId !== 'new') {
        await bottlingPlanService.updatePlan(planId, plan);
        setError(null);
      } else {
        const res = await bottlingPlanService.createPlan(plan);
        navigate(`/bottling-plans/${res.planID}`);
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      setError('Failed to save bottling plan: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Modal Event Handlers
  const handleAddItem = () => {
    setIsEditing(false);
    resetItemForm();
    setShowItemModal(true);
  };
  
  const handleEditItem = (item) => {
    setIsEditing(true);
    setCurrentItem({
      itemID: item.itemID,
      wineID: item.wineID.toString(),
      gallonsPlanned: item.gallonsPlanned,
      bottleID: item.bottleID.toString(),
      corkID: item.corkID?.toString() || '',
      capsuleID: item.capsuleID?.toString() || '',
      labelID: item.labelID?.toString() || '',
      notes: item.notes || '',
      finalAlc: item.finalAlc || '',
      brandID: item.brandID?.toString() || '',
      fancifulNameID: item.fancifulNameID?.toString() || '',
      version: item.version || ''
    });
    setShowItemModal(true);
  };
  
  const handleRemoveItem = (itemId) => {
    setItemToDelete(itemId);
    setShowDeleteConfirm(true);
  };
  
  const confirmRemoveItem = async () => {
    try {
      await bottlingPlanService.deleteItem(planId, itemToDelete);
      
      // Update the local state to remove the deleted item
      setPlan(prev => ({
        ...prev,
        items: prev.items.filter(item => item.itemID !== itemToDelete)
      }));
      
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error removing item:', err);
      setError('Failed to remove item: ' + err.message);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };
  
  const handleSaveItem = async (itemData) => {
    try {
      let currentPlanId = planId;
      
      // If this is a new plan, save it first
      if (!currentPlanId || currentPlanId === 'new') {
        const planData = {
          planDate: plan.planDate,
          status: plan.status,
          bottlingDate: plan.bottlingDate,
          performedBy: plan.performedBy,
          notes: plan.notes
        };
        
        const res = await bottlingPlanService.createPlan(planData);
        currentPlanId = res.planID;
        
        // Update URL to reflect the new plan ID
        navigate(`/bottling-plans/${currentPlanId}`, { replace: true });
      }
      
      // Prepare the item data with proper type conversion
      const preparedItem = {
        wineID: parseInt(itemData.wineID),
        gallonsPlanned: parseFloat(itemData.gallonsPlanned),
        bottleID: parseInt(itemData.bottleID),
        corkID: itemData.corkID ? parseInt(itemData.corkID) : null,
        capsuleID: itemData.capsuleID ? parseInt(itemData.capsuleID) : null,
        labelID: itemData.labelID && !itemData.labelOverride ? parseInt(itemData.labelID) : null,
        notes: itemData.notes,
        finalAlc: parseFloat(itemData.finalAlc) || null,
        brandID: itemData.brandID ? parseInt(itemData.brandID) : null,
        fancifulNameID: itemData.fancifulNameID ? parseInt(itemData.fancifulNameID) : null,
        version: itemData.version || null,
        // Custom label properties
        labelOverride: itemData.labelOverride || false,
        customLabelName: itemData.customLabelName || null
      };
      
      if (isEditing) {
        // Update existing item
        const casesEstimated = calculateEstimatedCases(preparedItem.gallonsPlanned, preparedItem.bottleID);
        const updatedItem = await bottlingPlanService.updateItem(currentPlanId, itemData.itemID, {
          ...preparedItem,
          casesEstimated
        });
        
        // Update local state to reflect changes
        setPlan(prev => ({
          ...prev,
          items: prev.items.map(item => 
            item.itemID === itemData.itemID ? { ...item, ...updatedItem } : item
          )
        }));
      } else {
        // Add new item
        const newItem = await bottlingPlanService.addItem(currentPlanId, preparedItem);
        
        // Update local state with the new item
        setPlan(prev => ({
          ...prev,
          items: [...prev.items, newItem]
        }));
      }
      
      // Close modal and reset form
      setShowItemModal(false);
      resetItemForm();
      setError(null);
    } catch (err) {
      console.error('Error saving item:', err);
      setError('Failed to save item: ' + err.message);
    }
  };
  
  const handleGenerateOrder = (items) => {
    setOrderItems(items);
    setShowOrderModal(true);
  };
  
  const handleOrderSuccess = () => {
    setShowOrderModal(false);
  };
  
  const resetItemForm = () => {
    setCurrentItem({
      wineID: '',
      gallonsPlanned: '',
      bottleID: '',
      corkID: '',
      capsuleID: '',
      labelID: '',
      notes: '',
      finalAlc: '',
      brandID: '',
      fancifulNameID: '',
      version: ''
    });
    setIsEditing(false);
    setSelectedMaterials([]);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Error display */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* Plan Header */}
        <BottlingPlanHeader 
          planId={planId} 
          onSave={handleSave} 
          saving={saving} 
        />
        
        {/* Plan Details */}
        <PlanDetailsForm 
          plan={plan} 
          setPlan={setPlan} 
          formErrors={formErrors} 
        />
        
        {/* Bottling Items Table */}
        <BottlingItemsTable 
          items={plan.items}
          wines={wines}
          onAddItem={handleAddItem}
          onEditItem={handleEditItem}
          onRemoveItem={handleRemoveItem}
          areDatesSet={areDatesSet}
          getDateWarningMessage={getDateWarningMessage}
          materialsByType={materialsByType}
        />
        
        {/* Materials Summary if there are items */}
        {plan.items.length > 0 && (
          <MaterialsSummary 
            items={plan.items}
            materialsByType={materialsByType}
            onGenerateOrder={handleGenerateOrder}
          />
        )}
      </form>

      {/* Modals */}
      <BottlingItemModal
        show={showItemModal}
        onClose={() => { setShowItemModal(false); resetItemForm(); }}
        onSave={handleSaveItem}
        item={currentItem}
        wines={wines}
        materialsByType={materialsByType}
        brands={brands}
        fancifulNames={fancifulNames}
        selectedMaterials={selectedMaterials}
        isEditing={isEditing}
        calculateEstimatedCases={calculateEstimatedCases} // Add this line
        />
      
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}
        onConfirmDelete={confirmRemoveItem}
        title="Confirm Removal"
        message="Are you sure you want to remove this item from the bottling plan?"
      />
      
      <MaterialOrderModal
        show={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        items={orderItems}
        onSuccess={handleOrderSuccess}
      />
    </div>
  );
};

export default BottlingPlanDetail;